import { isValidElement, type ReactNode } from 'react'
import type * as Yup from 'yup'

import type {
  Failable,
  FieldPath,
  FieldValues,
  Loadable,
  NonEmptyArray,
  NonEmptyFields,
  Optional,
  ValidatedKey,
} from '../types/types'

/**
 * Function for filtering null values from arrays to get the correct type afterwards
 * array: (string | null)[]
 * const newArray = arrayWithNulls.filter(notEmpty)
 */
export function notEmpty<T>(value: Optional<T>): value is NonNullable<T> {
  return value !== null && value !== undefined
}

/**
 * Checks that a nullable object exists and that selected fields are non-nullable.
 */
export const notEmptyFields =
  <K extends PropertyKey>(...requiredFields: readonly K[]) =>
  <T extends Partial<Record<K, unknown>>>(
    value: Optional<T>,
  ): value is NonNullable<T> & NonEmptyFields<Pick<T, K>> =>
    notEmpty(value) &&
    requiredFields.every((requiredField) => notEmpty(value[requiredField]))

/**
 * Function for checking null and undefined values to check whether something is/isn't empty
 * This function is to avoid double negations when checking for empty values.
 */
export function isEmpty<T>(value: Optional<T>): value is Optional<never> {
  return value === null || value === undefined
}

export const isNonEmptyArray = <T>(
  arr: Optional<T[]>,
): arr is NonEmptyArray<T> => Array.isArray(arr) && arr.length > 0

/**
 * Return `true` when `value` is "meaningful" for submission as a form value.
 *
 * A value is considered to be meaningful when it is not:
 * - Nullish (`null` or `undefined`)
 * - The empty string (`''`)
 * - An object without properties, after excluding `_type`.
 *
 * Note the implication: all falsy values will return `true` except for the empty string.
 * This includes `false` and the number zero (all `0`, `-0` and `0n`), which return `true`.
 */
export const isMeaningfulFormValue = <T>(value: T): boolean =>
  typeof value === 'object' && value !== null
    ? Object.keys(value).some((k) => k !== '_type')
    : notEmpty(value) && value !== ''

export const isError = (x: unknown): x is Error => x instanceof Error

export const isNotError = <T>(x: Failable<T>): x is T =>
  !isError(x) && x !== undefined

export const isLoading = (x: unknown): x is 'loading' => x === 'loading'

export const isLoaded = <T>(x: Loadable<T>): x is T =>
  !isError(x) && !isLoading(x) && x !== undefined

export const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * Cannot be export const, see https://github.com/microsoft/TypeScript/issues/34523.
 */
export function assertNotEmpty<T>(
  value: Optional<T>,
  message?: string,
): asserts value is T {
  if (isEmpty(value)) {
    throw new Error(
      message ??
        'Assertion failed: value was null or undefined when it should not have been',
    )
  }
}

type UseFormRegister<TFieldValues extends FieldValues> = <
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  name: TFieldName,
) => ValidatedKey

export function createValidator<
  TFieldValues extends FieldValues = never,
>(): UseFormRegister<TFieldValues> {
  return function validateKey(name: string) {
    return name as ValidatedKey
  }
}

export function createNestedValidator<TFieldValues extends FieldValues = never>(
  prefix: Optional<string>,
): UseFormRegister<TFieldValues> {
  return prefix
    ? function validateKey(name: string) {
        return `${prefix}.${name}` as ValidatedKey
      }
    : function validateKey(name: string) {
        return name as ValidatedKey
      }
}

export function uniqueBy<T>(
  a: T[],
  key: (val: T) => Optional<string | number>,
) {
  const seen: Record<string, boolean> = {}

  return a.filter((item) => {
    const k = key(item)

    if (k != undefined) {
      return Object.prototype.hasOwnProperty.call(seen, k)
        ? false
        : (seen[k] = true)
    }

    return false
  })
}

/**
 * Folds two values, picking the one that is non-empty, or returning `undefined` if both are.
 *
 * If both values are non-empty, the `folder` callback function is called to complete the reduction.
 *
 * This is useful when using `Array.reduce` on arrays containing optional items, where the `null`
 * value would be a meaningful output, so using `??` to provide a different non-null value for the
 * reducer implementation not be an option. For example:
 *
 *   const employerPremiums: Array<Optional<number>>
 *   const totalPremium = employerPremiums.reduce(
 *     (acc, premium) => foldNonEmpty(acc, premium, (a, b) => a + b),
 *     null as Optional<number>,
 *   )
 *
 * In the example above, `totalPremium` will be null-like iff all `employerPremiums` values were null-like.
 * This would allow the case where `employerPremiums` has some values that add up to `0` to be displayed
 * as `0`, and be presented in a different way than all premiums being null (e.g. "-"). Contrast this with:
 *
 *   const employerPremiums: Array<Optional<number>>
 *   const totalPremium = employerPremiums.reduce(
 *     (acc, premium) => acc + (premium ?? 0),
 *     0,
 *   )
 *
 * Where it would be impossible to determine whether `employerPremiums` actually summed up to `0` (by virtue
 * of having some values present) or all values were null-like.
 *
 * A third option is to call `Array.filter(notEmpty)` before using `Array.reduce`, but one would then need
 * to still pass `null` as the initial value and handle the possibility of the accumulator being `null`.
 */
export function foldNonEmpty<T>({
  left,
  right,
  folder,
}: {
  left: Optional<T>
  right: Optional<T>
  folder: (left: T, right: T) => T
}): Optional<T> {
  if (notEmpty(right)) {
    if (notEmpty(left)) {
      return folder(left, right)
    } else {
      return right
    }
  } else {
    return left
  }
}

/**
 * Update the first item in the array where `predicate` returns `true` (acts like `find`).
 *
 * If it can't be found, but the `initFn` was provided, the function will be used to initialize
 * and push the item to the array first, creating a new empty array if needed.
 *
 * If it's found (or it was added by `initFn`), it will be passed to `updateFn` which should
 * return the updated item.
 *
 * The array with the updated item is returned. If no `initFn` was provided and nothing was
 * updated, the original input array is returned (including when it's null or undefined).
 *
 * This function is useful when there is an `Array` acting like a `Map` (which occurs in certain
 * commands), and you want to avoid the back-and-forth transformation between `Map` and `Array`.
 *
 * It also simplifies the initialization of the array (as it can start as empty, and have new
 * items added), further reducing initialization logic. This also helps centralize that logic.
 *
 * No mutation happens in-place (so this function is safe to use with `useState`).
 */
export const updateItem = <T>({
  array,
  predicate,
  updateFn,
  initFn,
}: {
  array: Optional<T[]>
  predicate: (item: T) => boolean
  updateFn: (item: T) => T
  initFn?: () => T
}) => {
  const index = array?.findIndex(predicate)

  if (notEmpty(index)) {
    if (index === -1) {
      if (initFn) {
        return [...array!, updateFn(initFn())]
      } else {
        return array
      }
    } else {
      return array!
        .slice(0, index)
        .concat(
          [updateFn(structuredClone(array![index]))],
          array!.slice(index + 1),
        )
    }
  } else {
    if (initFn) {
      return [updateFn(initFn())]
    } else {
      return array
    }
  }
}

/**
 * Checks whether a particular key is known to exist in the given object.
 *
 * Useful to get additional type-safety while avoiding blind casts.
 */
export const hasField = <F extends string | number | symbol | ValidatedKey>(
  obj: unknown,
  field: F,
): obj is Record<F, unknown> =>
  typeof obj === 'object' && obj !== null && field in obj

/**
 * Similar to `hasField`, but checks that the field is also `notEmpty`.
 *
 * Unlike `hasField`, this utility type-checks that the field could exist in the object in the first place,
 * and the not-empty field will have the correct type instead of `unknown`.
 *
 * This can be seen as the non-lifted version of `notEmptyFields`.
 *
 * Not to be confused with `isEqual`.
 */
export const hasValue = <T extends object, F extends keyof T>(
  obj: T,
  field: F,
): obj is T & Record<F, NonNullable<T[F]>> => notEmpty(obj?.[field])

/**
 * Returns true if a given key exists in an object (works for objects with keys of type `string & {}` too).
 */
export const isKeyOfObject = <T extends Record<string, unknown>>(
  key: string | number | symbol,
  obj: T,
): key is keyof T => key in obj

/**
 * Returns true if a given unkown is a valid react node.
 */
export const isReactNode = (value: unknown): value is ReactNode =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  (typeof value === 'object' && isValidElement(value))

export const asOptional = <BASE, V extends BASE>(
  value: Optional<BASE>,
  assertion: (test: BASE) => test is V,
): value is Optional<V> => !value || assertion(value)

/** Creates a type-safe type-guard function for a given T. */
export const makeTypeGuard =
  <T extends Yup.AnyObject>(schema: Yup.ObjectSchema<T>) =>
  (object: unknown): object is T =>
    schema.isValidSync(object, { strict: true })
